import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Download,
  Eye,
  Ban,
  DollarSign,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge, getStatusVariant } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { formatCurrency, formatNumber, formatDate, formatRelativeTime, getInitials, maskPhone } from '@/lib/utils';
import toast from 'react-hot-toast';

interface User {
  id: string;
  name: string;
  phone: string;
  email?: string;
  avatar?: string;
  walletBalance: number;
  kycStatus: string;
  gamesPlayed: number;
  totalDeposits: number;
  status: 'active' | 'banned' | 'inactive';
  createdAt: string;
  lastLogin?: string;
}

// Empty data - will be fetched from API
const mockUsers: User[] = [];

export default function Users() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'banned' | 'inactive'>('all');
  const [kycFilter, setKycFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionModal, setActionModal] = useState<'ban' | 'balance' | null>(null);
  const [sortBy, setSortBy] = useState<{ field: string; order: 'asc' | 'desc' }>({ field: 'createdAt', order: 'desc' });

  const itemsPerPage = 10;

  // Filter users
  const filteredUsers = mockUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.phone.includes(search) ||
      user.email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesKyc = kycFilter === 'all' || user.kycStatus === kycFilter;
    return matchesSearch && matchesStatus && matchesKyc;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aVal = a[sortBy.field as keyof User];
    const bVal = b[sortBy.field as keyof User];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortBy.order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortBy.order === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  // Paginate
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field: string) => {
    setSortBy((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleBanUser = () => {
    if (selectedUser) {
      toast.success(`User ${selectedUser.name} has been banned`);
      setActionModal(null);
      setSelectedUser(null);
    }
  };

  const handleAdjustBalance = () => {
    if (selectedUser) {
      toast.success(`Balance adjusted for ${selectedUser.name}`);
      setActionModal(null);
      setSelectedUser(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">Users</h1>
          <p className="text-slate-400 mt-1">Manage platform users and their accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={formatNumber(mockUsers.length)} />
        <StatCard label="Active" value={formatNumber(mockUsers.filter((u) => u.status === 'active').length)} color="green" />
        <StatCard label="Banned" value={formatNumber(mockUsers.filter((u) => u.status === 'banned').length)} color="red" />
        <StatCard label="KYC Pending" value={formatNumber(mockUsers.filter((u) => u.kycStatus === 'pending').length)} color="amber" />
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={kycFilter}
              onChange={(e) => setKycFilter(e.target.value as any)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All KYC Status</option>
              <option value="approved">KYC Approved</option>
              <option value="pending">KYC Pending</option>
              <option value="rejected">KYC Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card variant="glass" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-white"
                  >
                    User
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Contact
                </th>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort('walletBalance')}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-white"
                  >
                    Balance
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  KYC
                </th>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort('gamesPlayed')}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-white"
                  >
                    Games
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Joined
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {paginatedUsers.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/users/${user.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                        {getInitials(user.name)}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-slate-500">ID: {user.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-3 h-3 text-slate-500" />
                        <span>{maskPhone(user.phone)}</span>
                      </div>
                      {user.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Mail className="w-3 h-3 text-slate-500" />
                          <span>{user.email}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-emerald-400">
                      {formatCurrency(user.walletBalance)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getStatusVariant(user.kycStatus)}>
                      {user.kycStatus.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span>{formatNumber(user.gamesPlayed)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getStatusVariant(user.status)}>{user.status}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm">{formatDate(user.createdAt)}</p>
                      {user.lastLogin && (
                        <p className="text-xs text-slate-500">
                          Last: {formatRelativeTime(user.lastLogin)}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/users/${user.id}`)}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedUser(user);
                          setActionModal('balance');
                        }}
                        title="Adjust Balance"
                      >
                        <DollarSign className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedUser(user);
                          setActionModal('ban');
                        }}
                        title={user.status === 'banned' ? 'Unban User' : 'Ban User'}
                        className={user.status === 'banned' ? 'text-emerald-400' : 'text-red-400'}
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
          <p className="text-sm text-slate-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, sortedUsers.length)} of {sortedUsers.length} users
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Ban User Modal */}
      <Dialog open={actionModal === 'ban'} onOpenChange={() => setActionModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.status === 'banned' ? 'Unban User' : 'Ban User'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.status === 'banned'
                ? `Are you sure you want to unban ${selectedUser?.name}?`
                : `Are you sure you want to ban ${selectedUser?.name}? They will be unable to access their account.`}
            </DialogDescription>
          </DialogHeader>
          {selectedUser?.status !== 'banned' && (
            <div className="py-4">
              <label className="text-sm font-medium text-slate-300">Reason for ban</label>
              <textarea
                className="mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                rows={3}
                placeholder="Enter reason for banning this user..."
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal(null)}>
              Cancel
            </Button>
            <Button
              variant={selectedUser?.status === 'banned' ? 'success' : 'destructive'}
              onClick={handleBanUser}
            >
              {selectedUser?.status === 'banned' ? 'Unban User' : 'Ban User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Modal */}
      <Dialog open={actionModal === 'balance'} onOpenChange={() => setActionModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Balance</DialogTitle>
            <DialogDescription>
              Adjust wallet balance for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-2">Current Balance</p>
              <p className="text-2xl font-bold text-emerald-400">
                {formatCurrency(selectedUser?.walletBalance || 0)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Adjustment Type</label>
              <select className="mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50">
                <option value="credit">Credit (Add)</option>
                <option value="debit">Debit (Remove)</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Amount</label>
              <Input
                type="number"
                placeholder="Enter amount"
                className="mt-2"
                icon={<DollarSign className="w-4 h-4" />}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Reason</label>
              <textarea
                className="mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                rows={2}
                placeholder="Enter reason for adjustment..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal(null)}>
              Cancel
            </Button>
            <Button onClick={handleAdjustBalance}>Adjust Balance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  const colorClasses = {
    green: 'text-emerald-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
  };

  return (
    <Card variant="glass" className="p-4">
      <p className="text-sm text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color ? colorClasses[color as keyof typeof colorClasses] : ''}`}>
        {value}
      </p>
    </Card>
  );
}
